import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Database, 
  FolderOpen, 
  BarChart3, 
  FileText, 
  Calendar,
  Clock,
  DollarSign,
  Award,
  BookOpen
} from 'lucide-react';

const RHDashboard = () => {
  const cards = [
    {
      title: 'Base de Datos',
      description: 'Gestión completa de empleados',
      icon: Database,
      color: 'from-blue-400 to-blue-600',
      path: '/rh/empleados',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600'
    },
    {
      title: 'Expedientes',
      description: 'Documentación de personal',
      icon: FolderOpen,
      color: 'from-purple-400 to-purple-600',
      path: '/rh/expedientes',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600'
    },
    {
      title: 'Estadísticas',
      description: 'Análisis y reportes',
      icon: BarChart3,
      color: 'from-green-400 to-green-600',
      path: '/rh/estadisticas',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600'
    },
    {
      title: 'Finiquitos',
      description: 'Gestión de liquidaciones',
      icon: FileText,
      color: 'from-orange-400 to-orange-600',
      path: '/rh/finiquitos',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600'
    },
    {
      title: 'Vacaciones',
      description: 'Control de días libres',
      icon: Calendar,
      color: 'from-pink-400 to-pink-600',
      path: '/rh/vacaciones',
      iconBg: 'bg-pink-100',
      iconColor: 'text-pink-600'
    },
    {
      title: 'Nómina',
      description: 'Próximamente',
      icon: DollarSign,
      color: 'from-teal-400 to-teal-600',
      path: '#',
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      proximamente: true
    },
    {
      title: 'Capacitación',
      description: 'Próximamente',
      icon: BookOpen,
      color: 'from-indigo-400 to-indigo-600',
      path: '#',
      iconBg: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      proximamente: true
    },
    {
      title: 'Evaluaciones',
      description: 'Próximamente',
      icon: Award,
      color: 'from-yellow-400 to-yellow-600',
      path: '#',
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      proximamente: true
    },
    {
      title: 'Incidencias',
      description: 'Próximamente',
      icon: Clock,
      color: 'from-red-400 to-red-600',
      path: '#',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      proximamente: true
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Encabezado */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition"
            >
              <ArrowLeft className="w-6 h-6 text-gray-700" />
            </motion.button>
          </Link>
          <div>
            <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
              <span className="text-5xl">👥</span>
              Recursos Humanos
            </h1>
            <p className="text-gray-600 mt-2">Gestión integral de personal</p>
          </div>
        </div>

        {/* Tarjetas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((card, index) => {
            const Icon = card.icon;
            
            return (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: card.proximamente ? 0 : -8 }}
              >
                {card.proximamente ? (
                  <div className="bg-white rounded-2xl shadow-lg transition-all duration-300 overflow-hidden opacity-75 cursor-not-allowed">
                    {/* Encabezado con gradiente */}
                    <div className={`h-32 bg-gradient-to-br ${card.color} p-6 relative overflow-hidden`}>
                      <div className="absolute -right-4 -top-4 w-32 h-32 bg-white opacity-10 rounded-full" />
                      <div className="absolute -right-8 -bottom-8 w-40 h-40 bg-white opacity-10 rounded-full" />
                      <div className={`${card.iconBg} w-14 h-14 rounded-xl flex items-center justify-center relative z-10`}>
                        <Icon className={`w-8 h-8 ${card.iconColor}`} />
                      </div>
                      <div className="absolute top-4 right-4 bg-white/90 px-3 py-1 rounded-full text-xs font-semibold text-gray-700">
                        🚧 Próximamente
                      </div>
                    </div>
                    
                    {/* Contenido */}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-800 mb-2">
                        {card.title}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {card.description}
                      </p>
                    </div>

                    {/* Footer */}
                    <div className="px-6 pb-6">
                      <div className="flex items-center text-gray-400 font-medium text-sm">
                        <span>En desarrollo</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link to={card.path}>
                    <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group">
                      {/* Encabezado con gradiente */}
                      <div className={`h-32 bg-gradient-to-br ${card.color} p-6 relative overflow-hidden`}>
                        <div className="absolute -right-4 -top-4 w-32 h-32 bg-white opacity-10 rounded-full" />
                        <div className="absolute -right-8 -bottom-8 w-40 h-40 bg-white opacity-10 rounded-full" />
                        <div className={`${card.iconBg} w-14 h-14 rounded-xl flex items-center justify-center relative z-10`}>
                          <Icon className={`w-8 h-8 ${card.iconColor}`} />
                        </div>
                      </div>
                      
                      {/* Contenido */}
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition">
                          {card.title}
                        </h3>
                        <p className="text-gray-600 text-sm">
                          {card.description}
                        </p>
                      </div>

                      {/* Footer con flecha */}
                      <div className="px-6 pb-6">
                        <div className="flex items-center text-blue-600 font-medium text-sm group-hover:gap-2 transition-all">
                          <span>Acceder</span>
                          <motion.span
                            animate={{ x: [0, 4, 0] }}
                            transition={{ repeat: Infinity, duration: 1.5 }}
                          >
                            →
                          </motion.span>
                        </div>
                      </div>
                    </div>
                  </Link>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Información adicional */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 bg-white rounded-xl shadow-lg p-6"
        >
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            📌 Información
          </h2>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-start gap-2">
              <span className="text-blue-500">•</span>
              <p className="text-gray-600">
                <strong>Base de Datos:</strong> Administra información de empleados
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-purple-500">•</span>
              <p className="text-gray-600">
                <strong>Expedientes:</strong> Almacena documentos importantes
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-green-500">•</span>
              <p className="text-gray-600">
                <strong>Estadísticas:</strong> Genera reportes y métricas
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RHDashboard;
